import { FC } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import type { TaskType } from "@myapp/contract";
import { FieldGroup, FieldWrapper, FormInput, ScheduleFields } from "../../../shared/ui";
import { AttachmentsSection } from "../../../shared/ui/controls/AttachmentsSection/AttachmentsSection";
import { SegmentedControl } from "../../../shared/ui/controls/SegmentedControl";
import { FormSelect } from "../../../shared/ui/controls/FormSelect";
import { HandwerkerSearchSelect } from "../../../entities/user";
import { taskStatusOptions } from "../../../entities/task";
import { taskHasActuals, type TaskFormValues } from "../model/taskForm.schema";

/**
 * Fields only — no queries, no mutations. Everything arrives as props from `UpsertTaskModal`,
 * which owns the data and the submit.
 *
 * Three sections, the same three the Termin form has: *Aufgabe* (what it is, who, in which
 * state, by when), *Zeit* (the reservation — `ScheduleFields`, shared with
 * `features/appointment-upsert` verbatim) and *Details*.
 *
 * **One shape for both task types.** The type is edited here like any other field and decides
 * nothing about the layout; it is a category for the list filter and the Arbeitszeit-Report. See
 * the rule block in `../model/taskForm.schema.ts`.
 *
 * WHAT GOES WHERE, and why it is not arbitrary: *Aufgabe* holds what every task has — Handwerker
 * (the only required field besides the title), Status and Abgabefrist. The Handwerker cannot live
 * under the Zeit heading, because it is required even when there is no Termin at all; the
 * Abgabefrist stays here for the same reason — it bounds the Aufgabe whether or not it is planned.
 *
 * ASTERISKS mark what is unconditionally required, so the Zeit fields carry none. The durations
 * *are* required — but only once a start is set, and a conditional rule is better told by the
 * inline error than by a mark that is wrong half the time.
 */

const TASK_TYPE_ITEMS: { value: TaskType; label: string }[] = [
  { value: "GENERAL_TASK", label: "Allgemeine Aufgabe" },
  { value: "ERRANDS", label: "Besorgung" },
];

const SERIES_SCOPE_LABELS = { single: "Nur diese Aufgabe", series: "Diese und alle folgenden" };

interface Props {
  control: Control<TaskFormValues>;
  mode: "create" | "edit";
  /** Edit mode: the task belongs to a series, so the scope toggle applies. */
  hasSeries: boolean;
  /** Current `plannedStart` — the anchor the recurrence preview is derived from. */
  plannedStart: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingAttachments: string[];
  onRemoveExistingAttachment: (index: number) => void;
}

export const TaskFormFields: FC<Props> = ({
  control,
  mode,
  hasSeries,
  plannedStart,
  files,
  onFilesChange,
  existingAttachments,
  onRemoveExistingAttachment,
}) => {
  /**
   * Read from the form rather than from the loaded task: the status is editable, so the Ist-Zeiten
   * have to appear in the same render as the user picking "Abgeschlossen". The mapper gates on the
   * same value — see `taskHasActuals`. Mirrors `AppointmentFormFields`.
   */
  const status = useWatch({ control, name: "status" });

  const deadlineField = (className: string) => (
    <FieldWrapper label="Abgabefrist" className={className}>
      <FormInput name="deadline" control={control} type="date" as="input" />
    </FieldWrapper>
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <FieldGroup title="Aufgabe">
        <Controller
          name="taskType"
          control={control}
          render={({ field }) => (
            <SegmentedControl
              size="sm"
              className="self-start"
              items={TASK_TYPE_ITEMS}
              value={field.value}
              onChange={field.onChange}
              ariaLabel="Aufgabentyp"
            />
          )}
        />

        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
          <FieldWrapper label="Titel" required className="col-span-2 sm:col-span-6">
            <FormInput name="title" control={control} as="input" />
          </FieldWrapper>

          <FieldWrapper label="Handwerker" required className="col-span-2 sm:col-span-3">
            <Controller
              name="assignedTechnicianId"
              control={control}
              render={({ field, fieldState }) => (
                <HandwerkerSearchSelect
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  error={fieldState.error?.message ?? null}
                  inputRef={field.ref}
                  placeholder="Handwerker suchen..."
                />
              )}
            />
          </FieldWrapper>

          {/*
            Handwerker | Status is the first row of both forms — whoever looks for the status finds
            it in the same place on a Termin and on an Aufgabe. Create has no status to choose, so
            the Abgabefrist takes the cell instead of leaving a hole; on edit it moves to its own row.
          */}
          {mode === "edit" ? (
            <>
              <FieldWrapper label="Status" className="col-span-2 sm:col-span-3">
                <FormSelect name="status" control={control} options={taskStatusOptions} />
              </FieldWrapper>
              {deadlineField("col-span-2 sm:col-span-3 sm:col-start-1")}
            </>
          ) : (
            deadlineField("col-span-2 sm:col-span-3")
          )}
        </div>
      </FieldGroup>

      <ScheduleFields
        control={control}
        mode={mode}
        showActuals={taskHasActuals(status)}
        hasSeries={hasSeries}
        plannedStart={plannedStart}
        hint="Ohne Termin bleibt die Aufgabe ungeplant und kann später im Kalender geplant werden."
        seriesScopeLabels={SERIES_SCOPE_LABELS}
      />

      <FieldGroup title="Details">
        <FieldWrapper label="Beschreibung">
          <FormInput name="description" control={control} as="textarea" rows={3} />
        </FieldWrapper>

        <AttachmentsSection
          existingAttachments={existingAttachments}
          onRemoveExistingAttachment={onRemoveExistingAttachment}
          files={files}
          onFilesChange={onFilesChange}
        />
      </FieldGroup>
    </div>
  );
};
